/**
 * Browser fingerprinting utility for guest session protection
 * Creates a unique but anonymous fingerprint of the browser
 * Uses multiple storage mechanisms for persistence
 */

// Storage keys
const STORAGE_KEY = "stylingcv-fp";
const SESSION_KEY = "stylingcv-fp-session";
const INDEXEDDB_NAME = "stylingcv-db";
const INDEXEDDB_STORE = "fingerprint";
const COOKIE_KEY = "stylingcv-fp";

// Components that make up the fingerprint
interface FingerprintComponents {
  userAgent: string;
  language: string;
  colorDepth: number;
  screenResolution: string;
  timezone: string;
  sessionStorage: boolean;
  localStorage: boolean;
  indexedDb: boolean;
  cpuClass?: string;
  platform: string;
  canvas?: string;
  webgl?: string;
  fonts?: string[];
}

/**
 * Generate a simple hash from a string
 */
function simpleHash(str: string): string {
  let hash = 0;
  if (str.length === 0) return hash.toString(16);
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).substring(0, 16);
}

/**
 * Get canvas fingerprint
 */
function getCanvasFingerprint(): string | undefined {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    canvas.width = 200;
    canvas.height = 50;

    ctx.textBaseline = "top";
    ctx.font = '14px "Arial"';
    ctx.fillStyle = "#f60";
    ctx.fillRect(0, 0, 200, 50);
    ctx.fillStyle = "#069";
    ctx.fillText("Browser Fingerprint: ", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Stylion", 4, 30);

    ctx.strokeStyle = "#ff0000";
    ctx.beginPath();
    ctx.arc(150, 25, 10, 0, Math.PI * 2);
    ctx.stroke();

    return canvas.toDataURL();
  } catch {
    return undefined;
  }
}

/**
 * Get WebGL fingerprint
 */
function getWebGLFingerprint(): string | undefined {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return undefined;

    const debugInfo = (gl as WebGLRenderingContext).getExtension("WEBGL_debug_renderer_info");
    if (debugInfo) {
      const vendor = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      return `${vendor}-${renderer}`;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Collect all fingerprint components
 */
function collectComponents(): FingerprintComponents {
  const screen = window.screen;
  const nav = navigator;

  return {
    userAgent: nav.userAgent,
    language: nav.language,
    colorDepth: screen.colorDepth,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    sessionStorage: !!window.sessionStorage,
    localStorage: !!window.localStorage,
    indexedDb: !!window.indexedDB,
    cpuClass: "cpuClass" in nav ? (nav as any).cpuClass : undefined,
    platform: nav.platform,
    canvas: getCanvasFingerprint(),
    webgl: getWebGLFingerprint(),
  };
}

/**
 * Generate a browser fingerprint
 */
export function generateFingerprint(): string {
  try {
    const components = collectComponents();
    const componentsString = JSON.stringify(components);
    return simpleHash(componentsString);
  } catch (error) {
    console.warn("Failed to generate fingerprint:", error);
    return simpleHash(Math.random().toString() + Date.now().toString());
  }
}

/**
 * Save fingerprint to cookie (fallback storage)
 */
function setCookieFingerprint(fingerprint: string): void {
  try {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1); // 1 year
    document.cookie = `${COOKIE_KEY}=${fingerprint};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  } catch {
    // Ignore
  }
}

/**
 * Get fingerprint from cookie
 */
function getCookieFingerprint(): string | null {
  try {
    const match = document.cookie.match(new RegExp(`${COOKIE_KEY}=([^;]+)`));
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Save fingerprint to sessionStorage
 */
function setSessionFingerprint(fingerprint: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, fingerprint);
  } catch {
    // Ignore
  }
}

/**
 * Get fingerprint from sessionStorage
 */
function getSessionFingerprint(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

/**
 * Save fingerprint to localStorage
 */
function setLocalFingerprint(fingerprint: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, fingerprint);
  } catch {
    // Ignore
  }
}

/**
 * Get fingerprint from localStorage
 */
function getLocalFingerprint(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save fingerprint to all storage mechanisms
 */
function saveFingerprint(fingerprint: string): void {
  setLocalFingerprint(fingerprint);
  setSessionFingerprint(fingerprint);
  setCookieFingerprint(fingerprint);
}

/**
 * Try to get fingerprint from any storage mechanism
 */
function getStoredFingerprint(): string | null {
  // Try localStorage first
  const local = getLocalFingerprint();
  if (local) return local;

  // Try sessionStorage
  const session = getSessionFingerprint();
  if (session) return session;

  // Try cookie
  const cookie = getCookieFingerprint();
  if (cookie) return cookie;

  return null;
}

/**
 * Get or create a persistent fingerprint for this browser
 * Uses multiple storage mechanisms for redundancy
 */
export function getPersistentFingerprint(): string {
  // Try to get existing fingerprint
  const existing = getStoredFingerprint();
  if (existing) {
    // Sync to all storage mechanisms (in case some were cleared)
    saveFingerprint(existing);
    return existing;
  }

  // Generate new fingerprint
  const fingerprint = generateFingerprint();
  saveFingerprint(fingerprint);
  return fingerprint;
}

/**
 * Clear the stored fingerprint from all storage mechanisms
 */
export function clearFingerprint(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore
  }
  try {
    document.cookie = `${COOKIE_KEY}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
  } catch {
    // Ignore
  }
}
